import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../utils/core/routes';
import { DashboardOfAsset, DashboardTypes } from '../../types/app/dashboard.types';
import { CustomDialog } from '../../components/core/Dialogs/CustomDialog';
import { Asset } from '../../types/app/asset.types';
import { useStyles } from './AssetForm.styles';
import { useTranslation } from '../../contexts/translation';
import BarChartIcon from '@mui/icons-material/BarChart';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useTheme } from '@mui/material/styles';
import TrafficIcon from '@mui/icons-material/Traffic';
import HttpIcon from '@mui/icons-material/Http';
import CommentIcon from '@mui/icons-material/Comment';
import StorageIcon from '@mui/icons-material/Storage';

interface Props {
  selectedAsset: Asset;
  dashboardData: DashboardOfAsset[];
  open: boolean;
  setOpen: any;
  handleAssetButtonActionClick: (
    dashboardType: string,
    dashboardId: number,
    dashboardUrl: string,
  ) => void;
}

export function AssetDashboardMenu({
  selectedAsset,
  dashboardData,
  open,
  setOpen,
  handleAssetButtonActionClick,
}: Props) {
  const navigate = useNavigate();
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <>
      {dashboardData && (
        <CustomDialog
          open={open}
          title={selectedAsset.name}
          titleClass={classes.assetModalCaption}
          maxWidth={'lg'}
          onClose={() => setOpen(false)}
        >
          <Grid container spacing={3}>
            <Grid item md={12} xs={12} mb={1}>
              <span className={classes.dashboardCaption}>{t('page.asset.modal-headline-1')}</span>
            </Grid>
            {dashboardData.map((dashboard: DashboardOfAsset) => {
              return (
                dashboard.type === DashboardTypes.MAIN_DASHBOARD && (
                  <Grid item lg={3} sm={4} xs={12} key={dashboard.id}>
                    <Box
                      boxShadow={2}
                      className={classes.menuItem}
                      onClick={() =>
                        handleAssetButtonActionClick(dashboard.type, dashboard.id, dashboard.url)
                      }
                    >
                      <BarChartIcon
                        sx={{
                          fontSize: '3.5em',
                          textAlign: 'center',
                          marginTop: '10px',
                          color: theme.palette.primary.main,
                        }}
                      />
                      <Typography className={classes.menuItemContent}>{dashboard.name}</Typography>
                    </Box>
                  </Grid>
                )
              );
            })}
            {dashboardData.find((x) => x.type === DashboardTypes.GRAFANA) &&
              dashboardData.map((dashboard: DashboardOfAsset) => {
                return (
                  dashboard.type === DashboardTypes.GRAFANA && (
                    <Grid item lg={3} sm={4} xs={12} key={dashboard.id}>
                      <Box
                        boxShadow={2}
                        className={classes.menuItem}
                        onClick={() =>
                          handleAssetButtonActionClick(dashboard.type, dashboard.id, dashboard.url)
                        }
                      >
                        <img
                          src={'/res/logo/grafana.svg'}
                          alt={'grafana logo'}
                          title={'grafana logo'}
                          style={{
                            fontSize: '3.5em',
                            textAlign: 'center',
                            marginTop: '10px',
                            color: theme.palette.info.main,
                            width: '25%',
                          }}
                        />
                        <Typography className={classes.menuItemContent} mt={0.5}>
                          {dashboard.name}
                        </Typography>
                      </Box>
                    </Grid>
                  )
                );
              })}
            {dashboardData.find((x) => x.type === DashboardTypes.EXTERNAL_URL) &&
              dashboardData.map((dashboard: DashboardOfAsset) => {
                return (
                  dashboard.type === DashboardTypes.EXTERNAL_URL && (
                    <Grid item lg={3} sm={4} xs={12} key={dashboard.id}>
                      <Box
                        boxShadow={2}
                        className={classes.menuItem}
                        sx={{ border: '2px solid' + theme.palette.info.main }}
                        onClick={() =>
                          handleAssetButtonActionClick(dashboard.type, dashboard.id, dashboard.url)
                        }
                      >
                        <HttpIcon
                          sx={{
                            fontSize: '3.5em',
                            textAlign: 'center',
                            marginTop: '10px',
                            color: theme.palette.primary.main,
                          }}
                        />
                        <Typography className={classes.menuItemContent} mt={0.5}>
                          {dashboard.name}
                        </Typography>
                      </Box>
                    </Grid>
                  )
                );
              })}
            {dashboardData.find((x) => x.type === DashboardTypes.OPC_DASHBOARD) &&
              dashboardData.map((dashboard: DashboardOfAsset) => {
                return (
                  dashboard.type === DashboardTypes.OPC_DASHBOARD && (
                    <Grid item lg={3} sm={4} xs={12} key={dashboard.id}>
                      <Box
                        boxShadow={2}
                        className={classes.menuItem}
                        sx={{ border: '2px solid' + theme.palette.info.main }}
                        onClick={() =>
                          handleAssetButtonActionClick(dashboard.type, dashboard.id, dashboard.url)
                        }
                      >
                        <img
                          src={'/res/logo/opc.png'}
                          alt={'opc logo'}
                          title={'opc logo'}
                          style={{
                            fontSize: '3.5em',
                            textAlign: 'center',
                            marginTop: '15px',
                            color: theme.palette.info.main,
                            width: '25%',
                          }}
                        />
                        <Typography className={classes.menuItemContent} mt={0.5}>
                          {dashboard.name}
                        </Typography>
                      </Box>
                    </Grid>
                  )
                );
              })}
          </Grid>
          <Grid container spacing={3} mt={1}>
            <Grid item md={12} xs={12}>
              <span className={classes.dashboardCaption}>{t('page.asset.modal-headline-3')}</span>
            </Grid>
            <Grid item sm={3} xs={12}>
              <Box
                boxShadow={2}
                className={classes.menuItem}
                onClick={() =>
                  navigate(
                    `${paths.ASSETS_LIST}/${selectedAsset?.id}/${encodeURIComponent(
                      selectedAsset?.name,
                    )}${paths.DASHBOARD_PERFORMANCE_HISTORY}`,
                  )
                }
              >
                <SpeedIcon
                  sx={{
                    fontSize: '3.5em',
                    textAlign: 'center',
                    marginTop: '10px',
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography className={classes.menuItemContent}>
                  {t('page.asset.modal-headline-3-item-1')}
                </Typography>
              </Box>
            </Grid>
            <Grid item sm={3} xs={12}>
              <Box
                boxShadow={2}
                className={classes.menuItem}
                onClick={() =>
                  navigate(
                    `${paths.ASSETS_LIST}/${selectedAsset?.id}/${encodeURIComponent(
                      selectedAsset?.name,
                    )}${paths.DASHBOARD_CYCLE_TIME_HISTORY}`,
                  )
                }
              >
                <AccessTimeIcon
                  sx={{
                    fontSize: '3.5em',
                    textAlign: 'center',
                    marginTop: '10px',
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography className={classes.menuItemContent}>
                  {t('page.asset.modal-headline-3-item-2')}
                </Typography>
              </Box>
            </Grid>
            <Grid item sm={3} xs={12}>
              <Box
                boxShadow={2}
                className={classes.menuItem}
                onClick={() =>
                  navigate(
                    `${paths.ASSETS_LIST}/${selectedAsset?.id}/${encodeURIComponent(
                      selectedAsset?.name,
                    )}${paths.DASHBOARD_STATE_HISTORY}`,
                  )
                }
              >
                <TrafficIcon
                  sx={{
                    fontSize: '3.5em',
                    textAlign: 'center',
                    marginTop: '10px',
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography className={classes.menuItemContent}>
                  {t('page.asset.modal-headline-3-item-3')}
                </Typography>
              </Box>
            </Grid>
            <Grid item sm={3} xs={12}>
              <Box
                boxShadow={2}
                className={classes.menuItem}
                onClick={() =>
                  navigate(
                    `${paths.ASSETS_LIST}/${selectedAsset?.id}/${encodeURIComponent(
                      selectedAsset?.name,
                    )}${paths.DASHBOARD_COMMENT}`,
                  )
                }
              >
                <CommentIcon
                  sx={{
                    fontSize: '3.5em',
                    textAlign: 'center',
                    marginTop: '10px',
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography className={classes.menuItemContent}>
                  {t('page.asset.modal-headline-3-item-4')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={3} mt={1}>
            <Grid item md={12} xs={12}>
              <span className={classes.dashboardCaption}>{t('page.asset.modal-headline-4')}</span>
            </Grid>
            <Grid item md={3} xs={3} className={classes.menuContainer}>
              <Box
                boxShadow={2}
                className={classes.menuItem}
                onClick={() =>
                  navigate(
                    paths.PLANNED_QTY_LIST +
                      '/' +
                      selectedAsset?.id +
                      '/' +
                      encodeURIComponent(selectedAsset?.name),
                  )
                }
              >
                <EventNoteIcon
                  sx={{
                    fontSize: '3.5em',
                    textAlign: 'center',
                    marginTop: '10px',
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography className={classes.menuItemContent}>
                  {t('page.asset.modal-headline-4-item-1')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CustomDialog>
      )}
    </>
  );
}
